import { Download, RefreshCw } from 'lucide-react'
import { Button, Chip, EmptyState } from '@/components/ui'
import type { Packet, PacketFile } from '../api/types'
import { ALL_DOC_TYPES, DOC_TYPE_LABELS, isBundledDocType } from '../utils/stage-meta'

export interface PacketFileListProps {
  packet: Packet
  onRegenerate: () => void
  regenerating: boolean
}

/**
 * Files grouped by doc_type, newest version first within each group.
 * `superseded` rows still render (greyed) so ops can see a regeneration
 * happened without losing the audit trail; only the top row per group is
 * the "current" one operators would actually open.
 */
export function PacketFileList({ packet, onRegenerate, regenerating }: PacketFileListProps) {
  const byDocType = new Map<string, PacketFile[]>()
  for (const f of packet.files) {
    const list = byDocType.get(f.doc_type) ?? []
    list.push(f)
    byDocType.set(f.doc_type, list)
  }
  for (const list of byDocType.values()) {
    list.sort((a, b) => (a.is_final === b.is_final ? b.version - a.version : a.is_final ? -1 : 1))
  }

  // The backend route itself doesn't gate on current stage (no 409),
  // but re-running generation while a generation job is already in
  // flight, or after finals have shipped / the packet is cancelled,
  // isn't a meaningful operator action — disable the button there.
  const canRegenerate = ![
    'draft_generating',
    'draft_regenerating',
    'finals_generating',
    'finals_ready',
    'cancelled',
  ].includes(packet.stage)

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border-default bg-surface p-4">
      <header className="flex items-center justify-between">
        <h3 className="text-role-section-label m-0">
          Documents ({packet.files.length})
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={onRegenerate}
          disabled={!canRegenerate || regenerating}
          loading={regenerating}
          title={
            canRegenerate
              ? 'Re-run draft generation for this shipment'
              : `Regenerate isn't available while the packet is ${packet.stage}`
          }
        >
          <RefreshCw size={14} className="mr-1.5" />
          Regenerate drafts
        </Button>
      </header>

      {packet.files.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No documents yet"
          description="Draft generation hasn't produced any files for this shipment yet."
        />
      ) : (
        <div className="flex flex-col gap-3">
          {ALL_DOC_TYPES.filter((dt) => byDocType.has(dt)).map((docType) => (
            <div key={docType} className="flex flex-col gap-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-primary">{DOC_TYPE_LABELS[docType]}</span>
                {isBundledDocType(docType) ? (
                  <Chip size="xs" variant="neutral">
                    Bundled from Odoo
                  </Chip>
                ) : null}
              </div>
              <ul className="flex flex-col divide-y divide-border-subtle rounded-sm border border-border-subtle">
                {(byDocType.get(docType) ?? []).map((file) => (
                  <FileRow key={file.id} packetId={packet.id} file={file} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function FileRow({ packetId, file }: { packetId: number; file: PacketFile }) {
  const href = `/api/export-docs/${packetId}/files/${file.id}`
  return (
    <li
      className={`flex items-center gap-3 px-3 py-2 text-sm ${file.superseded ? 'opacity-50' : ''}`}
    >
      <Chip size="xs" variant={file.is_final ? 'success' : 'info'}>
        {file.is_final ? 'Final' : 'Draft'}
      </Chip>
      <span className="text-secondary">v{file.version}</span>
      <span className="text-role-metadata text-tertiary">{file.generated_by}</span>
      {file.superseded ? (
        <span className="text-role-metadata text-tertiary">superseded</span>
      ) : null}
      <span className="text-role-metadata ml-auto text-tertiary">
        {formatDate(file.created_at)}
      </span>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-role-data-secondary text-info hover:underline"
      >
        <Download size={13} />
        Download
      </a>
    </li>
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
